"use client";

import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { Trash2 } from "lucide-react";
import { DeleteConfirmationDialog } from "./DeleteConfirmationDialog";
import { useSaveForLater } from "@/lib/stores/saveForLaterStore";
import { getProductById } from "@/static-stores/productStore";

interface DeleteButtonProps {
  itemId: string;
  itemTitle: string;
  productId: number; // Product ID to look up full product data
  onRemoveItem: (itemId: string) => void;
  variant?: "default" | "outline" | "ghost" | "destructive";
  size?: "sm" | "default" | "lg";
  className?: string;
  onSaveForLater?: (itemId: string, itemTitle: string) => void;
}

export function DeleteButton({
  itemId,
  itemTitle,
  productId,
  onRemoveItem,
  variant = "outline",
  size = "sm",
  className = "",
  onSaveForLater
}: DeleteButtonProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { addItem } = useSaveForLater();

  const handleDeleteClick = () => {
    setIsDialogOpen(true);
  };

  const handleCancel = () => {
    setIsDialogOpen(false);
  };

  const handleSaveForLater = (itemId: string, itemTitle: string) => {
    // Get the full product data from ProductStore
    const product = getProductById(productId);

    if (product) {
      addItem(product, 1);
      console.log(`✅ Saved "${itemTitle}" for later`);
    } else {
      console.warn(`❌ Could not find product data for ID "${productId}"`);
    }

    if (onSaveForLater) {
      onSaveForLater(itemId, itemTitle);
    }

    setIsDialogOpen(false);
  };

  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={handleDeleteClick}
        className={`text-red-600 hover:text-red-700 ${className}`}
        title="Remove item"
      >
        <Trash2 className="h-4 w-4" />
      </Button>

      <DeleteConfirmationDialog
        isOpen={isDialogOpen}
        itemTitle={itemTitle}
        itemId={itemId}
        onRemoveItem={onRemoveItem}
        onCancel={handleCancel}
        onSaveForLater={handleSaveForLater}
      />
    </>
  );
}
